import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConsentService, ConsentStatusModel, RecordConsentCommand } from '@goatsports/core/data-access';
import { Observable, catchError, fromEvent, map, of, tap } from 'rxjs';
import { RouteConstants } from '../constants/route.constant';
import { AuthenticationService } from './authentication.service';

/**
 * Client-side re-consent gate for the coach-portal. Fetches the current user's consent
 * status from Core (which proxies Authority) and routes to the full-page consent
 * interstitial when consent is required. Used by the route guard (app entry) and the
 * focus/visibility lifecycle hooks (mid-session re-check).
 */
@Injectable({ providedIn: 'root' })
export class ConsentGateService {
  private readonly consentService = inject(ConsentService);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  /** Last fetched status — the interstitial renders from this. */
  readonly status = signal<ConsentStatusModel | null>(null);

  refresh(): Observable<ConsentStatusModel> {
    return this.consentService.consentStatusGet().pipe(tap((status) => this.status.set(status)));
  }

  record(command: RecordConsentCommand): Observable<unknown> {
    return this.consentService.consentPost(command);
  }

  /**
   * Returns true when consent is satisfied; navigates to the consent route and returns
   * false when it is required. Fails open on error so a transient failure can't lock a
   * user out — they're re-checked on the next entry/focus.
   */
  checkAndGate(): Observable<boolean> {
    return this.refresh().pipe(
      map((status) => {
        if (status.consentRequired) {
          this.router.navigate([`/${RouteConstants.CONSENT_ROUTE}`]);
          return false;
        }
        return true;
      }),
      catchError(() => of(true)),
    );
  }

  /**
   * Re-check on tab focus / visibility — no interval poll. `visibilitychange` covers tab
   * switching and window minimize/restore; `window` `focus` covers returning from another
   * app while the browser stayed visible (where visibilitychange doesn't fire). Only runs
   * while logged in; the route guard covers login / initial entry.
   */
  initializeLifecycleChecks(): void {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (document.visibilityState === 'visible') {
            this.maybeCheck();
          }
        });
    }
    if (typeof window !== 'undefined') {
      fromEvent(window, 'focus')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.maybeCheck());
    }
  }

  private maybeCheck(): void {
    if (this.authenticationService.isUserLoggedIn()) {
      this.checkAndGate().subscribe();
    }
  }
}
